module Widgets.BetterSelect
    ( Select, SelectConfig, SelectState
    , selectWidget, selectWidgetWithId, initSelect, initSelect', preloadSelect, setSearchAff
    , initGroupedSelect, initGroupedSelect'
    , defSelectConfig, initSimpleSelect, initSimpleSelect'
    , selectedItem, setSelected, SelectOption, OptionGroup
    , SearchAff(..), FrontendSearchAff, BackendSearchAff, StaticSearchAff, SearchResult
    , GroupFunc, groupOptsByKey
    , simpleFrontendSearch, staticFrontendSearch, wrapSelectOption, wrapSelectOption', emptySearchAff
    , mapSearchAff, rankByLabel
) where

import Common

import Concur.Core.Patterns (loopState)
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Alt ((<|>))
import Control.Monad.Except (runExceptT)
import Data.Array (catMaybes, concat, filter, groupBy, mapWithIndex, sortWith, (!!))
import Data.Array as Arr
import Data.Array.NonEmpty (NonEmptyArray)
import Data.Array.NonEmpty as NE
import Data.Maybe (isJust, maybe)
import Data.String (toLower)
import Data.String as S
import Effect.Aff (Milliseconds(..), delay)
import Error (userError)
import GraphQL.Join (runGraphQL, runGraphQL_)
import GraphQL.Query (Query)
import Internal.Util (justIf)
import React.SyntheticEvent (SyntheticKeyboardEvent)
import Unsafe.Coerce (unsafeCoerce)
import Widgets.Loading (loadingWidget)

debounceDelay :: Milliseconds
debounceDelay = Milliseconds 300.0

type Select a =
    { id :: String
    , search :: String
    , searchAff :: SearchAff a
    , groupFunc :: Maybe (GroupFunc a)
    , config :: SelectConfig
    , selected :: Maybe (SelectOption a)
    , highlighted :: Maybe Int
    , expanded :: Boolean
    , hovering :: Boolean
    , loadNext :: Boolean
    -- | Options or the error that just occurred while loading.
    , options :: Array (OptionGroup a)
    , state :: SelectState
    }
data SelectState = SelLoading | SelError ErrorWrapper | SelOK
type SelectConfig =
    { placeholder :: Maybe String
    , deselectLabel :: Maybe String
    , allowDeselect :: Boolean
    , editable :: Boolean
    }
defSelectConfig :: SelectConfig
defSelectConfig =
    { placeholder: Nothing
    , deselectLabel: Nothing
    , allowDeselect: true
    , editable: true
    }

type OptionGroup a = { label :: String, opts :: Array (SelectOption a) }
type SelectOption a = { label :: String, value :: a }

initSelect' :: forall a. String -> SearchAff a -> SelectConfig -> Select a
initSelect' id searchAff config = initGroupedSelect' id searchAff Nothing config

initSelect :: forall a. String -> SearchAff a -> Select a
initSelect id searchAff = initSelect' id searchAff defSelectConfig

initGroupedSelect :: forall a. String -> SearchAff a -> GroupFunc a -> SelectConfig -> Select a
initGroupedSelect id aff group config = (initSelect' id aff config) { groupFunc = Just group }

initGroupedSelect' :: forall a. String -> SearchAff a -> Maybe (GroupFunc a) -> SelectConfig -> Select a
initGroupedSelect' id searchAff groupFunc config = 
    { id, searchAff, config, groupFunc
    , options: []
    , state: SelOK
    , search: ""
    , selected: Nothing
    , highlighted: Nothing
    , loadNext: true
    , expanded: false
    , hovering: false
    }

-- | Pre-loads a list of options into the select state. This means items will
-- | not be loaded unless search changes for backend search, and will not be
-- | loaded ever for frontend search.
preloadSelect :: forall a. Select a -> Array (SelectOption a) -> Select a
preloadSelect state opts = state { options = [{ label: "", opts }], loadNext = false }


-- | Replaces the `SearchAff` for a select widget with a different one.
-- |
-- | This will clear any loaded options and cause the select to re-load next
-- | time it's opened.
setSearchAff :: forall a. Select a -> SearchAff a -> Select a
setSearchAff state searchAff = state
    { options = []
    , searchAff = searchAff
    , loadNext = true
    }

initSimpleSelect' :: forall a. String -> Array (SelectOption a) -> SelectConfig -> Select a
initSimpleSelect' id options = initSelect' id $ staticFrontendSearch _.label options

initSimpleSelect :: forall a. String -> Array (SelectOption a) -> Select a
initSimpleSelect id options = initSimpleSelect' id options defSelectConfig


selectedItem :: forall a. Select a -> Maybe (SelectOption a)
selectedItem = _.selected

-- | Changes the selected item in the select widget to the given
-- | `SelectOption`. It is not required that this select option is in the list
-- | of current results.
setSelected :: forall a. Select a -> Maybe (SelectOption a) -> Select a
setSelected state opt = state { selected = opt, search = maybe "" _.label opt }

getItem :: forall a. Select a -> Int -> Maybe (SelectOption a)
getItem state idx = fromMaybe [] (_.opts <$> state.options !! 0) !! idx -- TODO: Handle groups


-- | This type defines how a `Select a` does searching. Searching could be done
-- | by sending a query string to the backend and getting the results, or by
-- | text-searching a predefined list of options.
data SearchAff a
    = FrontendSearch (FrontendSearchAff a)
    | BackendSearch (BackendSearchAff a)
    | StaticSearch (StaticSearchAff a)

-- | Function used to group search results in a `BetterSelect`
type GroupFunc a = Array (SelectOption a) -> Array (OptionGroup a)

-- | Create a `GroupFunc` which groups by the result of a function
groupOptsByKey :: forall a b. Ord b => (a -> b) -> (a -> String) -> GroupFunc a
groupOptsByKey func label opts =
    map labelGroup $ groupBy (\a b -> func a.value == func b.value) opts
  where
    labelGroup :: NonEmptyArray (SelectOption a) -> OptionGroup a
    labelGroup grouped = { label: label $ _.value $ NE.head grouped, opts: NE.toArray grouped }

-- | A search query which does the actual searching on the backend by sending a
-- | string keyword for each search.
type BackendSearchAff a = String -> SearchResult a

-- | A search query which fetches the entire list from the backend and then
-- | does the keyword searching on the frontend.
type FrontendSearchAff a =
    { query :: SearchResult a
    -- | Ranks how well the given select option matches the given string.
    , rank :: String -> SelectOption a -> Int
    }

-- | A search query which searches a static predefined list of items.
-- |
-- | Internally, a `FrontendSearchAff` will get transformed into this to cache
-- | the list after it's loaded.
type StaticSearchAff a =
    { items :: Array (SelectOption a)
    , rank :: String -> SelectOption a -> Int
    }

mapSearchAff :: forall a b. (a -> b) -> (String -> SelectOption b -> Int) -> SearchAff a -> SearchAff b
mapSearchAff f _ (BackendSearch sa) = BackendSearch (mapSearchResult f <<< sa)
mapSearchAff f rank (FrontendSearch sa) = FrontendSearch $ sa
        { query = mapSearchResult f sa.query
        , rank = rank
        }
mapSearchAff f rank (StaticSearch sa) = StaticSearch $ sa
        { items = map (\opt -> opt { value = f opt.value }) sa.items
        , rank = rank
        }

-- | The result of a search query.
type SearchResult a = Query (Array (SelectOption a))

mapSearchResult :: forall a b. (a -> b) -> SearchResult a -> SearchResult b
mapSearchResult f res = map (map func) res
  where
    func :: SelectOption a -> SelectOption b
    func opt = opt { value = f opt.value }

-- | Utility function for wrapping an `Aff (Either String (Array a))` into a
-- | `SearchResult a` using the given function to derive the labels.
wrapSelectOption :: forall a. (a -> String) -> Query (Array a) -> SearchResult a
wrapSelectOption label query = wrapSelectOption' label <$> query

-- | Utility function for wrapping an array into an array of select options
-- | with a function to derive the label.
wrapSelectOption' :: forall a. (a -> String) -> Array a -> Array (SelectOption a)
wrapSelectOption' label = map (\opt -> { label: label opt, value: opt })

-- | Search the results of a single query on the frontend using keyword string
-- | search on a single field.
simpleFrontendSearch :: forall a. (SelectOption a -> String) -> SearchResult a -> SearchAff a
simpleFrontendSearch key query = FrontendSearch { query, rank: \s -> stringMatch s <<< key }

-- | Search the results of a pre-defined list of items by string keyword.
staticFrontendSearch :: forall a.
    (SelectOption a -> String) -> Array (SelectOption a) -> SearchAff a
staticFrontendSearch key items = StaticSearch { items, rank: \s -> stringMatch s <<< key }


-- | A `SearchAff` which never returns any options.
emptySearchAff :: forall a. SearchAff a
emptySearchAff = StaticSearch { items: [], rank: \_ _ -> 0 }


rankByLabel :: forall a. String -> SelectOption a -> Int
rankByLabel s { label } = stringMatch s label


filterSort :: forall a. (SelectOption a -> Int) -> Array (SelectOption a) -> Array (SelectOption a)
filterSort rank = sortWith rank <<< filter (\o -> rank o > 0)

-- TODO: Better string matching
stringMatch :: String -> String -> Int
stringMatch "" _ = 1
stringMatch kw str | S.contains (S.Pattern $ toLower kw) (toLower str) = 1
stringMatch _ _ = 0




data SelectAction a
    = SelectOpt (SelectOption a)
    | Deselect
    | MoveHighlight Movement
    | EnterPressed
    | ChangeSearch String
    | Expanded Boolean
    | OnBlur
    | Hovering Boolean

data Movement = Up | Down | Absolute Int | Last

movePointer :: Int -> Movement -> Maybe Int -> Maybe Int
movePointer 0 _ _ = Nothing
-- Up
movePointer _ Up (Just ptr) | ptr > 0 = Just (ptr - 1)
movePointer len Up (Just _) = Just (len - 1)
movePointer _ Up Nothing = Just 0
-- Down
movePointer len Down (Just ptr) | ptr < len - 1 = Just (ptr + 1)
movePointer _ Down (Just _) = Just 0
movePointer _ Down Nothing = Just 0
-- Absolute
movePointer len (Absolute to) _ | to > len = Just (len - 1)
movePointer _ (Absolute to) _ | to < 0 = Just 0
movePointer _ (Absolute to) _ = Just to
-- Last
movePointer len Last _ = Just (len - 1)


-- | Call this to run the actual select widget.
-- |
-- | This will render the widgett and return a new `Select a` state when the
-- | selection changes.
-- |
-- | The current seletion can be accessed by calling `selectedItem`.
selectWidget :: forall a. Select a -> Widget HTML (Select a)
selectWidget state = loopState (state { expanded = false }) runSelect

-- | Runs the select widget as normal, but with a different ID.
selectWidgetWithId :: forall a. String -> Select a -> Widget HTML (Select a)
selectWidgetWithId id state@{ id: oldId } = _ { id = oldId } <$>
    selectWidget (state {id = id})


-- | Runs one iteration of the select widget.
-- |
-- | Returns `Right` if the selection changes and `Left` if not.
runSelect :: forall a. Select a -> Widget HTML (Either (Select a) (Select a))
runSelect state | not state.config.editable =
    handleAction state =<< render state
runSelect state | state.loadNext && state.expanded =
    loadSearch $ state { loadNext = false, search = search }
  where
    -- If the user hasn't changed the search input, load with no search. This
    -- prevents loading with the selected item as the search term.
    search = maybe "" (\opt -> if state.search == opt.label then "" else state.search) $
        selectedItem state
runSelect state =
    handleAction state =<< render state


handleAction :: forall a. Select a -> SelectAction a -> Widget HTML (Either (Select a) (Select a))

handleAction state (SelectOpt opt) =
    pure $ Right $ state
        { selected = Just opt
        , loadNext = true
        , expanded = false
        , search = opt.label }

handleAction state@{ config: { allowDeselect: false } } Deselect =
    pure $ Right $ state { search = "", loadNext = true, expanded = false }
handleAction state Deselect =
    pure $ Right $ state { selected = Nothing, search = "", loadNext = true, expanded = false }

handleAction state EnterPressed | state.expanded =
    case getItem state =<< state.highlighted of
        Just opt -> handleAction state $ SelectOpt opt
        Nothing -> handleAction state Deselect
handleAction state EnterPressed =
    pure $ Left $ state { expanded = true }

handleAction state (MoveHighlight move) = do
    let optcount = sum $ map (length <<< _.opts) state.options
    let ptr = movePointer optcount move state.highlighted
    pure $ Left $ state { highlighted = ptr, expanded = true }

-- HACK: When the mouse is hovering over the list and the input loses focus
-- (`onBlur`), we don't hide the list on the blur event. If we did, it would
-- prevent `onClick` from firing.
handleAction state (Expanded exp) =
    pure $ Left $ state { expanded = exp, hovering = false }
handleAction state OnBlur | state.hovering = pure $ Left state
handleAction state OnBlur = pure $ Left $
    state { expanded = false, hovering = false, search = search, loadNext = true }
  where
    -- Reset the text in the search box to the selection
    search = maybe "" _.label state.selected

handleAction state (Hovering hst) = pure $ Left $ state { hovering = hst }

handleAction state' (ChangeSearch search) =
    let state = state' { search = search }
    -- When the search changes, start a timer and re-render the widget.
    in ((Nothing <$ affAction (delay debounceDelay)) <|> (Just <$> render state)) >>=
    case _ of
        -- If the timer expires before another action is returned, start loading.
        Nothing -> loadSearch $ state { loadNext = false, expanded = true }
        -- If an action is returned first, handle it.
        Just act -> handleAction state act


data SearchLoadAction a
    = SelAct (SelectAction a)
    | SearchResult (Either ErrorWrapper (SearchAffResult a))

loadSearch :: forall a. Select a -> Widget HTML (Either (Select a) (Select a))
loadSearch state@{ searchAff: StaticSearch { items, rank } } =
    pure $ Left $ state
        { options = groupOpts state $ filterSort (rank state.search) items
        , state = SelOK
        }
loadSearch state = do
    act <- (SelAct <$> render (state { options = [], state = SelLoading }))
        <|> (SearchResult <$> affAction (loadSearchAff state.search state.searchAff))
    case act of
        SelAct a -> handleAction state a
        SearchResult (Right { result, searchAff }) ->
            pure $ Left $ state
                { options = groupOpts state result
                , searchAff = searchAff
                , state = SelOK
                }
        SearchResult (Left err) ->
            pure $ Left $ state
                { options = []
                , state = SelError err
                }

type SearchAffResult a =
    { result :: Array (SelectOption a)
    , searchAff :: SearchAff a
    -- ^ Updated searchAff
    }

loadSearchAff :: forall a. String -> SearchAff a -> Aff (Either ErrorWrapper (SearchAffResult a))
-- For a `BackendSearch` we send every query to the backend and get new results.
loadSearchAff search state@(BackendSearch searchAff) = runExceptT do
    result <- runGraphQL_ $ searchAff search
    pure { result, searchAff: state }
-- For a `FrontendSearch`, we load the options once and then turn it into a `StaticSearch`.
loadSearchAff search (FrontendSearch { query, rank }) = runGraphQL query >>= case _ of
    Right items -> loadSearchAff search $ StaticSearch { items, rank }
    Left err -> pure $ Left err
-- Static search is the simplest. Just filter/sort the list by the rank function.
loadSearchAff search state@(StaticSearch { items, rank }) = do
    let result = filterSort (rank search) items
    pure $ Right { result, searchAff: state }


groupOpts :: forall a. Select a -> Array (SelectOption a) -> Array (OptionGroup a)
groupOpts { groupFunc: Nothing } opts = [{ label: "", opts }]
groupOpts { groupFunc: Just func } opts = func opts


render :: forall a. Select a -> Widget HTML (SelectAction a)
render state = D.div
    [ P.key $ state.id <> "-wrapper"
    , P._id $ state.id <> "-wrapper"
    , P.className "widget-select-container"
    -- FIXME: The below is theoretically the correct way to detect focus in and
    -- out without detecting the user clicking on an item in the list as loss
    -- of focus. Unfortunately it doesn't seem to work.
    -- , Expanded true <$ P.unsafeMkPropHandler "onFocusIn"
    -- , Expanded false <$ P.unsafeMkPropHandler "onFocusOut"
    ] $ catMaybes
    [ Just $ renderInput state state.search
    , justIf (state.config.editable && state.expanded) $ renderList state
    ]

renderInput :: forall a. Select a -> String -> Widget HTML (SelectAction a)
renderInput selState search = D.div
    [ P.className $ if selState.config.editable then "widget-select" else "widget-select disabled" ] $ catMaybes
    [ Just $ D.input
        $ [ P.classList classes
          , P._id selState.id
          , P.autoComplete "off"
          , P.value value
          , P.placeholder $ fromMaybe (lookupTranslation "select-choose") selState.config.placeholder
          ]
        <> if selState.config.editable
            then
                [ ChangeSearch <$> P.unsafeTargetValue <$> P.onInput
                , handleKey <$> eventKey <$> P.filterProp canHandleKey P.onKeyDown
                , OnBlur <$ P.onBlur
                , Expanded true <$ P.onFocus
                ]
            else
                [ P.disabled true
                , P.tabIndex (-1)
                ]
    , justIf (selState.config.editable && isJust selState.selected) $ D.button
        [ P.className "widget-select-clear-button"
        , Deselect <$ P.onClick
        ]
        [ D.text "clear" ]
    ]
  where
    value = if selState.config.editable
        then search
        else fromMaybe search $ _.label <$> selState.selected

    keyList =
        [ 38, 40 -- up/down
        , 13 -- enter
        ]

    eventKey :: SyntheticKeyboardEvent -> Int
    eventKey e = (unsafeCoerce e).which

    canHandleKey :: SyntheticKeyboardEvent -> Boolean
    canHandleKey event = key `Arr.elem` keyList
      where key = (unsafeCoerce event).which

    handleKey :: Int -> SelectAction a
    handleKey 38 = MoveHighlight Up
    handleKey 40 = MoveHighlight Down
    handleKey 13 = EnterPressed
    handleKey _ = MoveHighlight Up -- should never happen due to `filterProp`

    classes :: Array (Maybe String)
    classes =
        [ Just "widget-select-input"
        , Just $ if isJust selState.selected
            then "selected"
            else "not-selected"
        ]


renderList :: forall a. Select a -> Widget HTML (SelectAction a)
renderList state@{ options, state: SelOK } = D.div [ P.className "widget-select-dropdown" ] $
    (if state.config.allowDeselect then [deselectOption] else []) <> concat (mapWithIndex listGroup options)
  where
    deselectOption :: Widget HTML (SelectAction a)
    deselectOption = D.a
        [ P.classList ([Just "widget-select-option"] <> activeClass)
        , P.href "javascript:void(0);"
        , Deselect <$ P.onClick
        , Hovering true <$ P.onMouseOver
        , Hovering false <$ P.onMouseOut
        ]
        [ D.text deselectLabel ]
      where activeClass = if state.highlighted == Just (-1) then [Just "active"] else []
    deselectLabel = fromMaybe
        (fromMaybe (lookupTranslation "select-choose") state.config.placeholder)
        state.config.deselectLabel

    renderGroups :: Int -> Int -> Array (OptionGroup a) -> Array (Widget HTML (SelectAction a))
    renderGroups _ _ [] = []
    renderGroups idx count list = case list !! idx of
        Just group -> listGroup count group <> renderGroups (idx + 1) (count + length group.opts) list
        Nothing -> []

    listGroup :: Int -> OptionGroup a -> Array (Widget HTML (SelectAction a))
    listGroup _ group | group.label == "" = mapWithIndex listItem group.opts
    listGroup _ group | length options < 2 = mapWithIndex listItem group.opts
    listGroup _ group = [D.div
        [ P.className "widget-select-group" ] $
        [ D.div [ P.className "widget-select-group-header" ]
            [ D.text group.label ]
        ] <> mapWithIndex listItem group.opts]

    listItem :: Int -> SelectOption a -> Widget HTML (SelectAction a)
    listItem idx opt = D.a
        [ P.classList ([Just "widget-select-option"] <> activeClass)
        , P.href "javascript:void(0);"
        , SelectOpt opt <$ P.onClick
        , Hovering true <$ P.onMouseOver
        , Hovering false <$ P.onMouseOut
        ]
        [ D.text $ opt.label ]
      where
        activeClass = if Just idx == state.highlighted || Just opt.label == (_.label <$> state.selected)
            then [Just "active"]
            else []
renderList { state: SelLoading } = D.div
    [ P.className "widget-select-dropdown widget-select-loading" ]
    [ loadingWidget ]
renderList { state: SelError error } = D.div
    [ P.className "widget-select-dropdown widget-select-error" ]
    [ D.text $ userError error ]
